type StartViewTransition = NonNullable<Document["startViewTransition"]>;

function isAbortLike(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "InvalidStateError" || error.name === "AbortError")
  );
}

function swallowAbort(promise: Promise<void>): Promise<void> {
  return promise.catch((error) => {
    if (!isAbortLike(error)) throw error;
  });
}

const WRAPPED = Symbol.for("healthos.abortSafeViewTransitions");

// Every document.startViewTransition() call in the app (React Router's own
// nav-driven ones and the ones useRevealSwap/Snapshot start by hand) passes
// through this one wrapper, making it the single place that can know whether
// a transition is currently in flight. WebKit has a real, severe bug where
// starting a transition while another hasn't fully settled internally yet can
// corrupt renderer state - callers that want to avoid that window check
// isViewTransitionActive() before calling startViewTransition themselves.
let activeCount = 0;

export function isViewTransitionActive(): boolean {
  return activeCount > 0;
}

// Allowlist, not a WebKit denylist: the primary target is the Tauri WKWebView,
// whose user agent is set by a shell outside this repo and can change without
// anything here failing. An agent we do not recognise must lose an animation,
// never crash the render process. jsdom is listed so unit tests still exercise
// the real wrapper below.
const SAFE_ENGINE = /(Chrome|Chromium|Edg|OPR|SamsungBrowser)\/|Gecko\/|jsdom\//;
// Every iOS browser is WKWebView regardless of the name it reports.
const WEBKIT_UNDER_ANOTHER_NAME = /(CriOS|FxiOS|EdgiOS)\//;

function hasSafeViewTransitions(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (WEBKIT_UNDER_ANOTHER_NAME.test(ua)) return false;
  return SAFE_ENGINE.test(ua);
}

// Run whatever startViewTransition was handed (a bare update callback, or the
// options object form { update }). Everything else on the options is animation
// config we are intentionally dropping.
function runUpdateCallback(arg: unknown): void {
  if (typeof arg === "function") {
    (arg as () => unknown)();
    return;
  }
  const update = (arg as { update?: () => unknown } | null | undefined)?.update;
  if (typeof update === "function") update();
}

// Neutralise view transitions on WebKit. The crash is a native render-process
// SIGSEGV that fires when navigation tears down the DOM while a same-document
// view transition is still compositing; three rounds of surgical guards did not
// close every path (React Router's own nav transitions were never among them),
// and it only ever reproduced on WebKit. `prefers-reduced-motion` avoids it
// solely because it collapses every transition to ~0ms, so we reproduce that
// safe state directly and unconditionally here: run the DOM update on a
// microtask (matching the native async-callback timing so no flushSync lands
// mid-render) and hand back an already-settled transition. No transition is
// ever in flight, so there is no window to crash in. Chrome, Firefox and Edge
// keep the real morph via the wrapper below. Revisit once WebKit's "Clear view
// transition on navigation" fix reaches a shipping Safari: re-test there, and
// drop this path if the crash is gone.
function installWebKitNoop(
  target: Document & { startViewTransition?: StartViewTransition & { [WRAPPED]?: boolean } },
): void {
  const noop = ((arg?: unknown) => {
    const settled = Promise.resolve().then(() => {
      try {
        runUpdateCallback(arg);
      } catch (error) {
        // Rejecting would resurrect the unhandled-rejection this module exists to
        // stop, so surface it as an uncaught error for RUM instead of dropping it.
        queueMicrotask(() => {
          throw error;
        });
      }
    });
    return {
      ready: settled,
      finished: settled,
      updateCallbackDone: settled,
      skipTransition() {},
    } as unknown as ReturnType<StartViewTransition>;
  }) as StartViewTransition & { [WRAPPED]?: boolean };
  noop[WRAPPED] = true;
  target.startViewTransition = noop;
}

// react-router chains transition.finished.finally() without a catch, so aborted
// transitions reject unhandled; wrap once so all consumers get abort-safe promises.
export function installAbortSafeViewTransitions(doc: Document = document): void {
  const target = doc as Document & {
    startViewTransition?: StartViewTransition & { [WRAPPED]?: boolean };
  };
  if (!target.startViewTransition || target.startViewTransition[WRAPPED]) return;
  if (!hasSafeViewTransitions()) {
    installWebKitNoop(target);
    return;
  }
  const original = target.startViewTransition.bind(doc) as StartViewTransition;
  const wrapped: StartViewTransition & { [WRAPPED]?: boolean } = (...args) => {
    activeCount += 1;
    const release = () => {
      activeCount = Math.max(0, activeCount - 1);
    };
    let transition: ReturnType<StartViewTransition>;
    try {
      transition = original(...args);
    } catch (error) {
      release();
      throw error;
    }
    const safe = new Map<string, Promise<void>>([
      ["ready", swallowAbort(transition.ready)],
      // "finished" is the transition's own settle signal - the same one
      // useRevealSwap uses to know when it's safe to clean up after itself.
      ["finished", swallowAbort(transition.finished).finally(release)],
      ["updateCallbackDone", swallowAbort(transition.updateCallbackDone)],
    ]);
    return new Proxy(transition, {
      get(t, prop) {
        if (typeof prop === "string" && safe.has(prop)) return safe.get(prop);
        // Receiver stays the native object so brand-checked getters keep working.
        const value = Reflect.get(t, prop, t);
        return typeof value === "function" ? value.bind(t) : value;
      },
    });
  };
  wrapped[WRAPPED] = true;
  target.startViewTransition = wrapped;
}
